import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Avatar, Popover, Box, Typography, Backdrop, CircularProgress } from '@mui/material';
import { MenuTwoTone, ExpandMore, Close } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProfileBox from './ProfileBox';
import { useAuth } from '../../../contexts/authContext';
import Logo from '../Logo/index';
import { styled } from '@mui/material/styles';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  height: '66px',
}));

const ProfileContainerStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  height: '60px',
  backgroundColor: theme.palette.primary.dark,
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
  marginLeft: 'auto',
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0.3),
  },
}));

const useStyles = makeStyles((theme) => ({
  profileAvatar: {
    marginRight: theme.spacing(1),
  },
  profileText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: theme.spacing(1),
  },
  emailText: {
    fontSize: '0.875rem',
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    maxWidth: '100%',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    backdropFilter: 'blur(2px)',
    backgroundColor: 'transparent',
  },
}));

const Header = ({ toggleDrawer, isDrawerOpen, role, roles, email, profileImage }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const { logout, initials, avatarColor, onSwitchRole } = useAuth();

  const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
  const handleProfileClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleProfileClose();
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (profileImage) {
      setLoadingImage(true)
      const img = new Image();
      img.src = profileImage;
      img.onload = () => {
        setLoadingImage(false);
        setImageLoaded(true);
      };
    }
  }, [profileImage]);

  const openProfile = Boolean(anchorEl);
  const id = openProfile ? 'simple-popover' : undefined;

  return (
    <>
      <AppBarStyled id="app-bar" position="fixed">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label={isDrawerOpen ? 'close drawer' : 'open drawer'}
              edge="start"
              onClick={toggleDrawer}
            >
              {isDrawerOpen ? <Close /> : <MenuTwoTone />}
            </IconButton>
          )}
          <Logo />
          <ProfileContainerStyled onClick={handleProfileClick}>
            {loadingImage ? (
              <CircularProgress size={40} style={{ color: avatarColor }} />
            ) : imageLoaded ? (
              <Avatar
                className={classes.avatar}
                src={profileImage}
                style={{ backgroundColor: avatarColor }}
              />
            ) : (
              <Avatar
                className={classes.avatar}
                style={{
                  backgroundColor: avatarColor,
                  width: 40,
                  height: 40,
                  fontSize: 18,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {initials}
              </Avatar>
            )}
            <Box className={classes.profileText}>
              <Box display="flex" alignItems="center">
                <Typography className={classes.emailText}>
                  {email}
                </Typography>
                <IconButton size="small" aria-label="expand" sx={{ color: theme.palette.common.white }}>
                  <ExpandMore />
                </IconButton>
              </Box>
              <Typography variant="body2" color="inherit">
                {role}
              </Typography>
            </Box>
          </ProfileContainerStyled>
          <Popover
            id={id}
            open={openProfile}
            anchorEl={anchorEl}
            onClose={handleProfileClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              style: { zIndex: 1100, marginTop: '-10px' },
            }}
          >
            <ProfileBox
              email={email}
              role={role}
              roles={roles}
              initials={initials}
              avatarColor={avatarColor}
              profileImage={profileImage}
              onLogout={handleLogout}
              onSwitchRole={onSwitchRole}
              handleProfileClose={handleProfileClose}
            />
          </Popover>
        </Toolbar>
      </AppBarStyled>
      <Backdrop
        open={openProfile}
        className={classes.backdrop}
        onClick={handleProfileClose}
      />
    </>
  );
};

export default Header;
